import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import * as z from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useDeleteAccountMutation } from '@/services/user/queries';
import { useAuthStore } from '@/store/auth';
import { useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';

const formSchema = z.object({
  password: z.string().min(1, 'Password is required')
});

interface DeleteAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function DeleteAccountDialog({
  open,
  onOpenChange
}: DeleteAccountDialogProps) {
  const userId = useAuthStore((state) => state.user?.id) || '';
  const clearAuthData = useAuthStore((state) => state.clearAuthData);
  const navigate = useNavigate();
  const posthog = usePostHog();
  
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: ''
    }
  });

  const { mutate: deleteAccount, isPending: isLoading } = useDeleteAccountMutation(
    userId,
    () => {
      posthog?.capture('form_submitted', { form_name: 'delete_account', success: true });
      posthog?.capture('modal_closed', { modal_name: 'delete_account', completed: true });
      toast.success('Your account has been permanently deleted.');
      form.reset();
      onOpenChange(false);
      clearAuthData();
      setTimeout(() => navigate('/sign-in', { replace: true }), 0);
    },
    (error) => {
      posthog?.capture('form_submitted', { form_name: 'delete_account', success: false });
      toast.error(error?.response?.data?.message || 'Failed to delete account. Please try again.');
    }
  );

  async function onSubmit(values: z.infer<typeof formSchema>) {
    deleteAccount(values.password);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Delete Account</DialogTitle>
          <DialogDescription>
            This action cannot be undone. This will permanently delete your
            account and remove your data from our servers.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            {/* Password field */}
            <FormField
              control={form.control}
              name='password'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm Password</FormLabel>
                  <FormControl>
                    <Input type='password' {...field} placeholder='Enter your password to confirm' />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => {
                  posthog?.capture('modal_closed', { modal_name: 'delete_account', completed: false });
                  onOpenChange(false);
                }}
              >
                Cancel
              </Button>
              <Button
                type='submit'
                variant='destructive'
                disabled={isLoading}
                trackingEvent='delete_account_submitted'
              >
                {isLoading ? 'Deleting...' : 'Delete Account'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}